// Mirror of backend/units.go. Keep in sync.
// Each unit has a category and a factor for converting to the category's
// canonical unit (g for mass, ml for volume, ea for count).

export type UnitCategory = 'mass' | 'volume' | 'count' | 'serving'

export type Unit = {
  code: string
  label: string
  category: UnitCategory
  factor: number
}

export const units: Unit[] = [
  // mass — canonical g
  { code: 'g',    label: 'g',     category: 'mass',   factor: 1 },
  { code: 'kg',   label: 'kg',    category: 'mass',   factor: 1000 },
  { code: 'oz',   label: 'oz',    category: 'mass',   factor: 28.3495 },
  { code: 'lb',   label: 'lb',    category: 'mass',   factor: 453.592 },

  // volume — canonical ml
  { code: 'ml',   label: 'ml',    category: 'volume', factor: 1 },
  { code: 'l',    label: 'l',     category: 'volume', factor: 1000 },
  { code: 'tsp',  label: 'tsp',   category: 'volume', factor: 4.92892 },
  { code: 'tbsp', label: 'tbsp',  category: 'volume', factor: 14.7868 },
  { code: 'cup',  label: 'cup',   category: 'volume', factor: 236.588 },
  { code: 'floz', label: 'fl oz', category: 'volume', factor: 29.5735 },
  { code: 'gal',  label: 'gal',   category: 'volume', factor: 3785.41 },

  // count — canonical ea
  { code: 'ea',    label: 'ea',    category: 'count', factor: 1 },
  { code: 'clove', label: 'clove', category: 'count', factor: 1 },
  { code: 'leaf',  label: 'leaf',  category: 'count', factor: 1 },
  { code: 'head',  label: 'head',  category: 'count', factor: 1 },
  { code: 'sprig', label: 'sprig', category: 'count', factor: 1 },
  { code: 'slice', label: 'slice', category: 'count', factor: 1 },
  { code: 'pinch', label: 'pinch', category: 'count', factor: 1 },

  // serving — recipe components / lots
  { code: 'serving', label: 'serving', category: 'serving', factor: 1 },
]

// Format a canonical quantity (g for mass, ml for volume, ea for count) for
// display, honoring the user's unit-system preference. Canonical values are how
// the backend stores required/on-hand amounts; this is the single place that
// renders them, so both the Cook Reconcile modal and the Pantry shortfall line
// stay consistent. With no prefs (or 'metric') it falls back to the canonical
// metric ladder; 'imperial' converts to oz/lb and tsp/fl oz/cup.
export function fmtCanon(
  qty: number,
  cat: string,
  prefs?: { mass?: 'metric' | 'imperial'; volume?: 'metric' | 'imperial' },
): string {
  const trim = (s: string) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s)
  if (cat === 'mass') {
    if (prefs?.mass === 'imperial') {
      return qty >= 453.592
        ? `${trim((qty / 453.592).toFixed(2))} lb`
        : `${trim((qty / 28.3495).toFixed(1))} oz`
    }
    return qty >= 1000 ? `${(qty / 1000).toFixed(2)} kg` : `${Math.round(qty)} g`
  }
  if (cat === 'volume') {
    if (prefs?.volume === 'imperial') {
      if (qty < 14.7868) return `${trim((qty / 4.92892).toFixed(1))} tsp`
      if (qty < 236.588) return `${trim((qty / 29.5735).toFixed(1))} fl oz`
      return `${trim((qty / 236.588).toFixed(2))} cup`
    }
    return qty >= 1000 ? `${(qty / 1000).toFixed(2)} l` : `${Math.round(qty)} ml`
  }
  return `${qty} ea`
}

const byCode = new Map(units.map(u => [u.code, u]))

export function lookupUnit(code: string): Unit | undefined {
  return byCode.get(code)
}

export function categoryOf(code: string | undefined): UnitCategory | undefined {
  if (!code) return undefined
  return byCode.get(code)?.category
}

export function unitsForCategory(cat: UnitCategory | undefined): Unit[] {
  if (!cat) return units
  return units.filter(u => u.category === cat)
}

// Units grouped into the three categories the picker UIs render. 'serving' is a
// recipe-component-only unit and is intentionally excluded — it's never a valid
// raw ingredient / pantry / shopping unit, and the dropdowns don't show it.
export function groupUnitsForPicker(): Record<'mass' | 'volume' | 'count', Unit[]> {
  const grouped: Record<'mass' | 'volume' | 'count', Unit[]> = { mass: [], volume: [], count: [] }
  for (const u of units) {
    if (u.category in grouped) grouped[u.category as 'mass' | 'volume' | 'count'].push(u)
  }
  return grouped
}
